import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch'
import {
  Box, Button, Form, Heading, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle, Stack, Text, TextLink,
  VisuallyHidden,
} from '@ui'
import { deleteJSON, patchJSON } from '../api.js'
import { formatDate, formatTakenAt, formatTime, ukInputToISO, ukInputValue } from '../format.js'

// Images come through the server (they're private): /api/photos/:id/{thumb|display|original}.
// For a video, thumb and display are a frame from it and original is the video itself.
export const photoUrl = (id, size) => `/api/photos/${id}/${size}`

// Where the gallery, viewer and map pins load images from: the signed-in API by default, a public
// walk link's own endpoints on a shared page (SharedWalk.jsx).
export const PhotoUrlContext = createContext(photoUrl)
const usePhotoUrl = () => useContext(PhotoUrlContext)

const isVideo = (p) => p.media_type === 'video'
const noun = (p) => (isVideo(p) ? 'video' : 'photo')

// 83000 → "1:23"
export function formatClipLength(ms) {
  if (ms == null) return ''
  const s = Math.round(ms / 1000)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const pad = (n) => String(n).padStart(2, '0')
  return h ? `${h}:${pad(m)}:${pad(s % 60)}` : `${m}:${pad(s % 60)}`
}

// A square thumbnail button. A video gets a play badge and its length; a photo or video the
// browser couldn't make a preview of (has_preview false) gets a plain tile.
export function PhotoTile({ photo, label, title, onClick }) {
  const photoUrl = usePhotoUrl()
  return (
    <button type="button" className="photo-thumb" onClick={onClick} aria-label={label} title={title}>
      {photo.has_preview === false
        ? <span className="photo-placeholder" aria-hidden="true">{isVideo(photo) ? 'Video' : 'Photo'}</span>
        : <img src={photoUrl(photo.id, 'thumb')} alt="" loading="lazy" width={photo.width ?? undefined} height={photo.height ?? undefined} />}
      {isVideo(photo) && (
        <span className="photo-video-badge" aria-hidden="true">
          <svg viewBox="0 0 10 10" width="0.6em" height="0.6em" fill="currentColor"><path d="M1 0.5v9l8-4.5z" /></svg>
          {photo.duration_ms != null && ` ${formatClipLength(photo.duration_ms)}`}
        </span>
      )}
    </button>
  )
}

// A small camera, for walks with photos in the walks table and calendar.
export function CameraIcon({ count, ...style }) {
  const label = count === 1 ? '1 photo' : `${count} photos`
  return (
    <Text className="photo-marker" muted title={label} {...style}>
      <svg viewBox="0 0 16 16" width="1em" height="1em" fill="currentColor" aria-hidden="true">
        <path d="M5.5 2.5 4.6 4H2.5A1.5 1.5 0 0 0 1 5.5v7A1.5 1.5 0 0 0 2.5 14h11a1.5 1.5 0 0 0 1.5-1.5v-7A1.5 1.5 0 0 0 13.5 4h-2.1l-.9-1.5zM8 6a3 3 0 1 1 0 6 3 3 0 0 1 0-6m0 1.3a1.7 1.7 0 1 0 0 3.4 1.7 1.7 0 0 0 0-3.4" />
      </svg>
      <VisuallyHidden>{label}</VisuallyHidden>
    </Text>
  )
}

export function VideoIcon({ count, ...style }) {
  const label = count === 1 ? '1 video' : `${count} videos`
  return (
    <Text className="photo-marker" muted title={label} {...style}>
      <svg viewBox="0 0 16 16" width="1em" height="1em" fill="currentColor" aria-hidden="true">
        <path d="M1.5 4A1.5 1.5 0 0 0 0 5.5v5A1.5 1.5 0 0 0 1.5 12h8a1.5 1.5 0 0 0 1.5-1.5V9.4l3.2 2.1a.5.5 0 0 0 .8-.4V4.9a.5.5 0 0 0-.8-.4L11 6.6V5.5A1.5 1.5 0 0 0 9.5 4z" />
      </svg>
      <VisuallyHidden>{label}</VisuallyHidden>
    </Text>
  )
}

// A walk's markers in the walks table and calendar (2026-10-09): a camera when it has photos and
// a video camera when it has videos, each with its count in the tooltip. photo_count counts both.
export function MediaIcons({ walk }) {
  const videos = walk.video_count ?? 0
  const photos = (walk.photo_count ?? 0) - videos
  return (
    <>
      {photos > 0 && <CameraIcon count={photos} ms={1} />}
      {videos > 0 && <VideoIcon count={videos} ms={1} />}
    </>
  )
}

// Most walk names are the app's default ("Thu 18 May, 13:48"), which says less than the date, so
// the viewer names a walk by its date and time, plus its name only if it was given one.
const DEFAULT_NAME = /^[A-Z][a-z]{2} \d{1,2} [A-Z][a-z]{2,3}, \d{2}:\d{2}$/
export function walkCaption(p) {
  const when = `${formatDate(p.start_time)}, ${formatTime(p.start_time)}`
  return DEFAULT_NAME.test(p.walk_name) ? when : `${p.walk_name} · ${when}`
}

const monthLabel = (iso) => new Date(iso).toLocaleDateString('en-GB', { timeZone: 'Europe/London', month: 'long', year: 'numeric' })

// Photos (newest walk first, as /api/photos sends them) by month of their walk: a heading, one
// grid, then links to the month's walks with their counts. Clicking a thumbnail calls onOpen with
// its index in photos. The photos page's date view, and its map's list for a chosen place; also a
// shared Photos page, where caption names a walk and its dates aren't links (linkWalks false).
export function PhotoMonths({ photos, onOpen, headingLevel = 2, caption = walkCaption, linkWalks = true }) {
  // [{ month, walks: [{ walk_id, walk_name, start_time, items: [{ photo, i }] }] }]
  const months = useMemo(() => {
    const out = []
    photos.forEach((p, i) => {
      const month = monthLabel(p.start_time)
      if (out.at(-1)?.month !== month) out.push({ month, walks: [] })
      const walks = out.at(-1).walks
      if (walks.at(-1)?.walk_id !== p.walk_id) walks.push({ ...p, items: [] })
      walks.at(-1).items.push({ photo: p, i })
    })
    return out
  }, [photos])
  return months.map(({ month, walks }) => (
    <Box as="section" key={month} mb={4}>
      <Heading level={headingLevel} size={5}>{month}</Heading>
      <div className="photo-grid">
        {walks.flatMap((w) => w.items.map(({ photo, i }, n) => (
          <PhotoTile
            key={photo.id} photo={photo} onClick={() => onOpen(i)} title={caption(w)}
            label={`Open ${noun(photo)} ${n + 1} of ${w.items.length} from ${caption(w)}`}
          />
        )))}
      </div>
      <Text as="p" text="small" muted mt={1} mb={0}>
        {walks.map((w, n) => (
          <span key={w.walk_id}>
            {n > 0 && ' · '}
            {linkWalks
              ? <TextLink to={`/walks/${w.walk_id}`} linkTone="muted">{formatDate(w.start_time)}</TextLink>
              : formatDate(w.start_time)} ({w.items.length})
          </span>
        ))}
      </Text>
    </Box>
  ))
}

// A grid of square thumbnails; clicking one calls onOpen with its index.
export function PhotoGrid({ photos, onOpen }) {
  return (
    <div className="photo-grid">
      {photos.map((p, i) => (
        <PhotoTile key={p.id} photo={p} onClick={() => onOpen(i)} label={`Open ${noun(p)} ${i + 1} of ${photos.length}`} />
      ))}
    </div>
  )
}

// "Taken 16:25" in the viewer footer, with a way to set the time by hand (UK time, to the second)
// for files whose own time is missing or wrong, and to go back to the file's own time. The time
// places the photo on the walk's map and chart. onChange(fields) gets the photo's new time fields.
function TakenTime({ photo, walkStart, onChange }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  useEffect(() => { setEditing(false); setError(null) }, [photo.id])

  const save = async (iso) => {
    setSaving(true)
    setError(null)
    try {
      await patchJSON(`/api/photos/${photo.id}`, { taken_at: iso })
      onChange({
        taken_at: iso ?? photo.file_taken_at ?? null,
        taken_at_set_by_hand: iso != null,
      })
      setEditing(false)
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  if (!editing) {
    return (
      <Stack inline align="center" gap={2} tone="onDarkMuted">
        {photo.taken_at ? `Taken ${formatTakenAt(photo.taken_at, walkStart)}${photo.taken_at_set_by_hand ? ' (set by hand)' : ''}` : 'No time'}
        {onChange && (
          <Button
            variant="link" size="sm" p={0}
            onClick={() => { setValue(ukInputValue(photo.taken_at ?? walkStart ?? new Date().toISOString())); setEditing(true) }}
          >
            {photo.taken_at ? 'Change time' : 'Set time'}
          </Button>
        )}
      </Stack>
    )
  }
  const iso = ukInputToISO(value)
  return (
    <Form
      display="inline-flex" wrap align="center" gap={2}
      onSubmit={(e) => { e.preventDefault(); if (iso) save(iso) }}
    >
      <Label htmlFor="photo-taken-at" mb={0}>Taken (UK time)</Label>
      <Input
        id="photo-taken-at" type="datetime-local" step="1" size="sm" value={value}
        onChange={(e) => setValue(e.target.value)} style={{ width: 'auto' }} autoFocus
      />
      <Button type="submit" size="sm" disabled={!iso || saving}>Save</Button>
      {photo.taken_at_set_by_hand && (
        <Button onDark size="sm" disabled={saving} onClick={() => save(null)}>
          {photo.file_taken_at ? `Use the file's time (${formatTakenAt(photo.file_taken_at, walkStart)})` : 'Remove the time'}
        </Button>
      )}
      <Button onDark size="sm" disabled={saving} onClick={() => setEditing(false)}>Cancel</Button>
      {error && <Text tone="danger">Couldn't save: {error}</Text>}
    </Form>
  )
}

// Full-screen view of photos[index], with previous/next (buttons, arrow keys, swipe), a link to the
// original, delete (onDeleted(id) after a soft delete), the taken time (set by hand through
// onUpdated(id, fields); walkStart is where the time editor starts for a photo with no time), and
// (showWalk) the walk it was taken on, a link, or plain text from walkLabel(photo) on a shared page.
// Videos play in place, streamed with seeking. index null = closed.
// A video in the viewer. Until it's first played it has no browser controls, only our own centre
// play button: browsers don't reliably draw theirs (Chrome and Firefox often showed none on the
// first video opened after moving to another walk), and Firefox's can't be hidden with CSS, so
// leaving the controls off is the only way not to show two. After that the browser's own take over.
function ViewerVideo({ photo, label }) {
  const photoUrl = usePhotoUrl()
  const ref = useRef(null)
  const [started, setStarted] = useState(false)
  const start = () => {
    setStarted(true)
    // A click is a user action, so playing with sound is allowed. If it fails (a format the
    // browser can't decode), the browser's controls now show its own error state.
    ref.current?.play().catch(() => {})
  }
  return (
    <>
      <video
        ref={ref} src={photoUrl(photo.id, 'original')} controls={started} playsInline preload="metadata"
        poster={photo.has_preview === false ? undefined : photoUrl(photo.id, 'display')}
        aria-label={label} onClick={started ? undefined : start}
      />
      {!started && (
        <button type="button" className="video-play" onClick={start} aria-label={`Play ${label.toLowerCase()}`}>
          <svg viewBox="0 0 10 10" aria-hidden="true"><path d="M2.5 1v8l6.5-4z" /></svg>
          {photo.duration_ms != null && <span>{formatClipLength(photo.duration_ms)}</span>}
        </button>
      )}
    </>
  )
}

// A photo in the viewer that zooms and pans (2026-11-03): pinch, double-tap / double-click (fit ↔
// 2.5× at that point), mouse wheel or trackpad pinch about the pointer, drag to pan when zoomed,
// kept inside the photo's edges. From 1× up to the best image's own pixels at about two screen
// pixels each (at least 2×, at most 10×). Signed in (allowOriginal), once past 1.5× the original
// loads in the background and replaces the 1600 px display copy in place once decoded; if it
// fails (or the browser can't show its format) the display copy stays. Shared pages never fetch
// originals, so they zoom the display copy. zoomRef gets the library's controls (zoomIn, zoomOut,
// resetTransform, panBy) for the viewer's keys and buttons; onZoomed(bool) when it leaves or
// returns to 1×. Mounted per photo (key), so a new photo starts at 1×.
const DISPLAY_LONG_SIDE = 1600
const ORIGINAL_FROM_SCALE = 1.5

function ZoomablePhoto({ photo, alt, allowOriginal, zoomRef, onZoomed }) {
  const photoUrl = usePhotoUrl()
  const boxRef = useRef(null)
  const [box, setBox] = useState(null)
  const [natural, setNatural] = useState(
    photo.width && photo.height ? { w: photo.width, h: photo.height } : null,
  )
  const [src, setSrc] = useState(() => photoUrl(photo.id, 'display'))
  const [full, setFull] = useState('no') // no | loading | done | failed
  const [zoomed, setZoomed] = useState(false)
  const [wantOriginal, setWantOriginal] = useState(false)
  const loader = useRef(null)
  // Double-tap while zoomed: the library's own double tap does start its reset, but with panning
  // on (zoomed) its touch-end handling then cancels it, so a double tap back to fit is done here,
  // just after. (Double-tap to zoom in, and double-click either way, are the library's.)
  const taps = useRef({ start: null, last: null })
  const onTouchStart = (e) => {
    taps.current.start = e.touches.length === 1
      // Whether zoomed when this tap began: by its end the library may already be zooming in.
      ? { x: e.touches[0].clientX, y: e.touches[0].clientY, t: Date.now(), zoomed } : null
  }
  const onTouchEnd = (e) => {
    const start = taps.current.start
    taps.current.start = null
    const t = e.changedTouches[0]
    if (!start || e.touches.length || !t) return
    const isTap = Date.now() - start.t < 250 && Math.hypot(t.clientX - start.x, t.clientY - start.y) < 10
    if (!isTap) { taps.current.last = null; return }
    const last = taps.current.last
    if (last && Date.now() - last.t < 300 && Math.hypot(t.clientX - last.x, t.clientY - last.y) < 40) {
      taps.current.last = null
      if (start.zoomed) setTimeout(() => zoomRef.current?.resetTransform(200), 30)
    } else {
      taps.current.last = { x: t.clientX, y: t.clientY, t: Date.now() }
    }
  }

  useLayoutEffect(() => {
    const el = boxRef.current
    if (!el) return undefined
    const measure = () => setBox({ w: el.clientWidth, h: el.clientHeight })
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Mouse wheel and trackpad pinch, our own: the library adds step × the event's pixel delta to
  // the scale, so one mouse-wheel notch (~100 px) jumped to full zoom at any useful step. Here each
  // ~100 px of wheel multiplies the scale by ~1.22 about the pointer, so trackpad pinches (small
  // deltas, ctrlKey) zoom smoothly and a mouse takes ~10 notches from fit to 7×.
  const maxScaleRef = useRef(2)
  // The scale last asked for: events come faster than the 80 ms animation, and the library's
  // state lags until it ends, so a burst builds on the target, not the scale on screen.
  const wheelTarget = useRef({ scale: 1, at: 0 })
  useEffect(() => {
    const el = boxRef.current
    if (!el) return undefined
    const onWheel = (e) => {
      const zoom = zoomRef.current
      if (!zoom) return
      e.preventDefault()
      // Read deltaY before deltaMode: Firefox then reports pixels; lines (deltaMode 1) count as
      // 33 px, so a notch is ~100 px in every browser.
      const dy = e.deltaY
      const px = dy * (e.deltaMode === 1 ? 33 : e.deltaMode === 2 ? 800 : 1)
      const factor = Math.min(2, Math.max(0.5, Math.exp(-px * (e.ctrlKey ? 0.01 : 0.002))))
      const now = performance.now()
      const scale = now - wheelTarget.current.at < 150 ? wheelTarget.current.scale : zoom.instance.state.scale
      const next = Math.min(maxScaleRef.current, Math.max(1, scale * factor))
      wheelTarget.current = { scale: next, at: now }
      if (Math.abs(next - zoom.instance.state.scale) < 0.001) return
      if (next <= 1) zoom.resetTransform(80)
      else zoom.zoomToPoint(next, e.clientX, e.clientY, 80)
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [zoomRef])

  // The photo fitted inside the viewer: the zoomed content, so panning stops at its edges.
  const fitted = useMemo(() => {
    if (!box || !natural || !box.w || !box.h) return null
    const aspect = natural.w / natural.h
    return box.w / box.h > aspect ? { w: box.h * aspect, h: box.h } : { w: box.w, h: box.w / aspect }
  }, [box, natural])

  // A new size (rotating a phone, resizing a window) starts again from fitted and centred.
  useEffect(() => { zoomRef.current?.resetTransform(0) }, [fitted?.w, fitted?.h, zoomRef])

  const maxScale = useMemo(() => {
    if (!fitted || !natural) return 2
    const displayW = Math.min(natural.w, natural.w * DISPLAY_LONG_SIDE / Math.max(natural.w, natural.h))
    const realW = allowOriginal ? natural.w : displayW
    const cssPerImagePx = 2 / (window.devicePixelRatio || 1)
    return Math.min(10, Math.max(2, (realW * cssPerImagePx) / fitted.w))
  }, [fitted, natural, allowOriginal])
  maxScaleRef.current = maxScale

  // The original, once zoomed past ORIGINAL_FROM_SCALE; never preloaded, and abandoned (its
  // download cancelled) when the viewer moves to another photo.
  useEffect(() => {
    if (!allowOriginal || !wantOriginal || full !== 'no') return
    const url = photoUrl(photo.id, 'original')
    const img = new Image()
    loader.current = img
    setFull('loading')
    img.src = url
    img.decode()
      .then(() => { if (loader.current === img) { setSrc(url); setFull('done') } })
      .catch(() => { if (loader.current === img) setFull('failed') })
  }, [allowOriginal, wantOriginal, full, photo.id, photoUrl])
  useEffect(() => () => {
    if (loader.current) { loader.current.src = ''; loader.current = null }
  }, [])

  return (
    <div ref={boxRef} className="photo-zoom-box" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <TransformWrapper
        ref={zoomRef}
        minScale={1}
        maxScale={maxScale}
        centerOnInit
        centerZoomedOut
        limitToBounds
        doubleClick={{ mode: zoomed ? 'reset' : 'zoomIn', step: 1.5, animationTime: 200 }}
        wheel={{ disabled: true }}
        panning={{ disabled: !zoomed }}
        onTransform={(_, state) => {
          const z = state.scale > 1.01
          if (z !== zoomed) { setZoomed(z); onZoomed?.(z) }
          if (state.scale > ORIGINAL_FROM_SCALE && !wantOriginal) setWantOriginal(true)
        }}
      >
        <TransformComponent
          wrapperClass="photo-zoom-wrapper"
          contentStyle={fitted ? { width: fitted.w, height: fitted.h } : undefined}
        >
          <img
            src={src} alt={alt} draggable={false} className="photo-zoom-img"
            onLoad={(e) => {
              if (!natural) setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })
            }}
          />
        </TransformComponent>
      </TransformWrapper>
      {full === 'loading' && <Text className="photo-zoom-note" text="small">Loading full resolution…</Text>}
    </div>
  )
}

export function PhotoViewer({ photos, index, onIndex, onDeleted, onUpdated, walkStart, showWalk = false, walkLabel = null, readOnly = false }) {
  const photoUrl = usePhotoUrl()
  const open = index != null && photos[index] != null
  const photo = open ? photos[index] : null
  const [confirming, setConfirming] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState(null)
  useEffect(() => { setConfirming(false); setDeleteError(null) }, [photo?.id])

  const remove = async () => {
    setDeleting(true)
    setDeleteError(null)
    try {
      await deleteJSON(`/api/photos/${photo.id}`)
      setConfirming(false)
      onDeleted?.(photo.id)
    } catch (err) {
      setDeleteError(err.message)
    } finally {
      setDeleting(false)
    }
  }
  const go = useCallback(
    (step) => { if (open) onIndex((index + step + photos.length) % photos.length) },
    [open, index, photos.length, onIndex],
  )
  const touchX = useRef(null)
  // Zoom (photos only): the library's controls, and whether the photo is zoomed past 1×. While
  // zoomed, a one-finger drag and the arrow keys pan instead of changing photo.
  const zoomRef = useRef(null)
  const [zoomed, setZoomed] = useState(false)
  useEffect(() => { setZoomed(false) }, [photo?.id])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => {
      if (e.target instanceof HTMLVideoElement) return // its own arrow keys seek
      if (e.target instanceof HTMLInputElement) return // the time editor
      const zoom = zoomRef.current
      if (zoom && (e.key === '+' || e.key === '=')) { zoom.zoomIn(0.5); return }
      if (zoom && e.key === '-') { zoom.zoomOut(0.5); return }
      if (zoom && e.key === '0') { zoom.resetTransform(); return }
      if (zoom && zoomed) {
        const pan = { ArrowLeft: [120, 0], ArrowRight: [-120, 0], ArrowUp: [0, 120], ArrowDown: [0, -120] }[e.key]
        if (pan) { e.preventDefault(); zoom.panBy(pan[0], pan[1]) }
        return
      }
      if (e.key === 'ArrowLeft') go(-1)
      if (e.key === 'ArrowRight') go(1)
    }
    window.addEventListener('keydown', onKey)
    // Load the neighbours so stepping through is quick.
    for (const step of [-1, 1]) {
      const next = photos[(index + step + photos.length) % photos.length]
      if (next && next.has_preview !== false) new Image().src = photoUrl(next.id, 'display')
    }
    return () => window.removeEventListener('keydown', onKey)
  }, [open, index, photos, go, zoomed, photoUrl])

  return (
    <Modal
      show={open} onHide={() => onIndex(null)} fullscreen dark className="photo-viewer" panelClassName="photo-viewer-panel"
      label={photo ? noun(photo) : 'Photo'}
    >
      {photo && (
        <>
          <ModalHeader borderless py={2}>
            <ModalTitle text="md" truncate>
              {photos.length > 1 && <Text me={2}>{index + 1} of {photos.length}</Text>}
              {walkLabel && <Text>{walkLabel(photo)}</Text>}
              {showWalk && !walkLabel && (
                <TextLink to={`/walks/${photo.walk_id}`} onClick={() => onIndex(null)}>
                  {walkCaption(photo)}
                </TextLink>
              )}
            </ModalTitle>
          </ModalHeader>
          <ModalBody
            className="photo-viewer-body"
            onTouchStart={(e) => {
              // A swipe changes photo only at 1× and with one finger (not a pinch, not a pan).
              touchX.current = e.target instanceof HTMLVideoElement || zoomed || e.touches.length > 1
                ? null : e.touches[0].clientX
            }}
            onTouchMove={(e) => { if (e.touches.length > 1) touchX.current = null }}
            onTouchEnd={(e) => {
              if (touchX.current == null) return
              const dx = e.changedTouches[0].clientX - touchX.current
              touchX.current = null
              if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1)
            }}
          >
            {isVideo(photo) ? (
              <ViewerVideo key={photo.id} photo={photo} label={`Video ${index + 1} of ${photos.length}`} />
            ) : photo.has_preview === false ? (
              <Text as="p" tone="onDarkMuted">No preview of this photo. Download it below.</Text>
            ) : (
              <ZoomablePhoto
                key={photo.id} photo={photo} alt={`Photo ${index + 1} of ${photos.length}`}
                allowOriginal={!readOnly} zoomRef={zoomRef} onZoomed={setZoomed}
              />
            )}
            {photos.length > 1 && (
              <>
                <Button variant="dark" className="photo-nav photo-prev" onClick={() => go(-1)} aria-label="Previous">‹</Button>
                <Button variant="dark" className="photo-nav photo-next" onClick={() => go(1)} aria-label="Next">›</Button>
              </>
            )}
            {/* Over the photo, not in the footer: a footer that grew would shrink the photo's box,
                and a new box size starts the zoom again from fitted. */}
            {zoomed && (
              <Stack inline align="center" gap={2} className="photo-zoom-controls">
                <Button variant="dark" size="sm" onClick={() => zoomRef.current?.zoomOut(1)}>Zoom out</Button>
                <Button variant="dark" size="sm" onClick={() => zoomRef.current?.resetTransform()}>Fit to screen</Button>
              </Stack>
            )}
          </ModalBody>
          <ModalFooter borderless py={2} justify="center" gap={3} text="small">
            {(!readOnly || photo.taken_at) && (
              <TakenTime
                photo={photo} walkStart={walkStart ?? photo.start_time}
                onChange={!readOnly && onUpdated ? (fields) => onUpdated(photo.id, fields) : undefined}
              />
            )}
            {/* A shared page offers no originals: they can carry the camera's details and place. */}
            {!readOnly && (
              <TextLink href={`${photoUrl(photo.id, 'original')}?download=1`}>
                Download {isVideo(photo) ? 'video' : 'original'}
                {photo.width ? ` (${photo.width} × ${photo.height}${isVideo(photo) && photo.duration_ms != null ? `, ${formatClipLength(photo.duration_ms)}` : ''})` : ''}
              </TextLink>
            )}
            {onDeleted && !readOnly && !confirming && (
              <Button variant="link" size="sm" p={0} linkTone="danger" onClick={() => setConfirming(true)}>Delete</Button>
            )}
            {confirming && (
              <Stack inline align="center" gap={2}>
                Delete this {noun(photo)} from the site?
                <Button variant="danger" size="sm" onClick={remove} disabled={deleting}>Delete</Button>
                <Button onDark size="sm" onClick={() => setConfirming(false)} disabled={deleting}>Cancel</Button>
              </Stack>
            )}
            {deleteError && <Text tone="danger">Couldn't delete: {deleteError}</Text>}
          </ModalFooter>
        </>
      )}
    </Modal>
  )
}
